import { useCallback } from 'react';

import { t, Trans, plural } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import Download from '@decisiv/iconix/lib/components/Download';
import ExclamationTriangleF from '@decisiv/iconix/lib/components/ExclamationTriangleF';

import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import { useBulkCreateContactsContext } from '../context/BulkCreateContactsContext';
import { CountsContainer, SummaryRow } from './styles';

const Summary = () => {
  const { summary, reportUrl, failedToResolve } =
    useBulkCreateContactsContext();

  const handleDownloadReportClick = useCallback(() => {
    /* istanbul ignore next -- @preserve */
    if (!reportUrl) return;

    window.location.assign(reportUrl);
  }, [reportUrl]);

  // Polling stopped without a resolved server outcome (repeated errors / timeout).
  if (failedToResolve || !summary) {
    return (
      <Flex flexDirection="column">
        <H3>
          <Trans>Summary</Trans>
        </H3>
        <Flex marginTop={1}>
          <Alert intent="warning" title={t`Import status unavailable`}>
            <Trans>
              We couldn&apos;t confirm the result of your upload. It may still
              be processing — check the contacts list in a few minutes before
              retrying.
            </Trans>
          </Alert>
        </Flex>
      </Flex>
    );
  }

  const added = summary.successful ?? 0;
  const failed = summary.failed ?? 0;
  const isAllSuccess = added > 0 && failed === 0;
  const isAllFailed = added === 0 && failed > 0;

  return (
    <Flex flexDirection="column">
      <H3>
        <Trans>Summary</Trans>
      </H3>

      <SummaryRow>
        <CountsContainer>
          {isAllFailed && (
            <Flex alignItems="center">
              <ExclamationTriangleF
                color={toColorString(color.status.danger.medium)}
                size="small"
              />
              <P shade={1} marginLeft={0.5}>
                {plural(failed, {
                  one: `The contact failed to be added.`,
                  other: `All ${failed} contacts failed to be added.`,
                })}
              </P>
            </Flex>
          )}

          {isAllSuccess && (
            <Flex alignItems="center">
              <CheckCircleF
                color={toColorString(color.status.success.medium)}
                size="small"
              />
              <P shade={1} marginLeft={0.5}>
                {plural(added, {
                  one: `${added} Contact successfully added.`,
                  other: `${added} Contacts successfully added.`,
                })}
              </P>
            </Flex>
          )}

          {!isAllSuccess && !isAllFailed && (
            <>
              <P shade={1}>
                {plural(added, {
                  one: `${added} Contact successfully added.`,
                  other: `${added} Contacts successfully added.`,
                })}
              </P>
              <P shade={1}>
                {plural(failed, {
                  one: `${failed} Contact failed to be added.`,
                  other: `${failed} Contacts failed to be added.`,
                })}
              </P>
            </>
          )}
        </CountsContainer>
        <Button
          text={t`Download Report`}
          size="small"
          kind="secondary"
          icon={Download}
          disabled={!reportUrl}
          onClick={handleDownloadReportClick}
        />
      </SummaryRow>
    </Flex>
  );
};

export default Summary;
